import { useCallback, useRef } from "react";
import { Ramp } from "../../../../../../types";
import { useReferenceAircraftState } from "../../../RampEditor/stores/useReferenceAircraftState";
import { adjustSquare, getCenter, snapSquare } from "../math";
import { DraggingPoint, DraggingShape, Point, Shape } from "../RampCanvasV2";
import { calculateSnapPositions, GuideLine, rotateSpotToAngle } from "../spotSnapping";
import { updateAtId } from "../utils";

type Props = {
  selectedTool: string;
  setSelectedTool: (tool: string) => void;
  shapes: Shape[];
  setShapes: React.Dispatch<React.SetStateAction<Shape[]>>;
  currentShape: Shape;
  setDraggingPoint: React.Dispatch<React.SetStateAction<DraggingPoint>>;
  setDraggingShape: React.Dispatch<React.SetStateAction<DraggingShape>>;
  draggingPoint: DraggingPoint;
  draggingShape: DraggingShape;
  canvasRef: React.RefObject<HTMLCanvasElement>;
  ramp: Ramp;
  isShiftPressed: boolean;
  setMousePosition: React.Dispatch<
    React.SetStateAction<{ x: number; y: number }>
  >;
  getActiveShapes: () => Shape[];
  pixelsToFeetRatio: number;
  setSnapGuideLines: React.Dispatch<React.SetStateAction<GuideLine[]>>;
};

export const useOnMouseMove = (props: Props) => {
  const {
    selectedTool,
    shapes,
    setShapes,
    setDraggingShape,
    draggingPoint,
    draggingShape,
    canvasRef,
    isShiftPressed,
    setMousePosition,
    getActiveShapes,
    pixelsToFeetRatio,
    setSnapGuideLines,
  } = props;

  // Track last mouse position for drag delta calculation
  const lastMousePos = useRef<{ x: number; y: number } | null>(null);

  return useCallback(
    (event: React.MouseEvent) => {
      if (selectedTool === "Measuring") {
        return;
      }

      const canvas = canvasRef.current;
      const rect = canvas?.getBoundingClientRect();
      if (!rect || !canvas) {
        return;
      }

      // Scale mouse coordinates to match canvas internal resolution
      // This handles cases where canvas.width differs from CSS display width (zoom, DPR, etc.)
      const scaleX = canvas.width / rect.width;
      const scaleY = canvas.height / rect.height;
      const x = (event.clientX - rect.left) * scaleX;
      const y = (event.clientY - rect.top) * scaleY;
      setMousePosition({ x, y });

      // Handle reference aircraft dragging
      // IMPORTANT: Read directly from store using getState() to get current value,
      // not stale closure value from when callback was created
      const refState = useReferenceAircraftState.getState();
      if (refState.isDragging) {
        if (lastMousePos.current) {
          const dx = x - lastMousePos.current.x;
          const dy = y - lastMousePos.current.y;
          // Convert delta from pixels to feet
          const dxFeet = dx / pixelsToFeetRatio;
          const dyFeet = dy / pixelsToFeetRatio;
          refState.setPosition({
            x: refState.position.x + dxFeet,
            y: refState.position.y + dyFeet,
            angle: refState.position.angle,
          });
        }
        lastMousePos.current = { x, y };
        return;
      } else {
        lastMousePos.current = null;
      }

      if (draggingPoint) {
        const { shapeId, pointIndex } = draggingPoint;
        if (shapeId) {
          const updatedShape = shapes.find((s) => s.id === shapeId);
          if (
            updatedShape.type === "Tie Down T" ||
            updatedShape.type === "Double T" ||
            updatedShape.type === "Circle" ||
            updatedShape.type === "Helipad" ||
            updatedShape?.type === "Compass Rose"
          ) {
            // Fixed adjustment logic for square
            const center = getCenter(updatedShape.points);
            const adjustedPoints = adjustSquare(
              updatedShape.points,
              pointIndex,
              { x, y },
              center
            );
            // if shift is pressed, snap to 45 degree angles
            if (isShiftPressed) {
              //  TODO: implement here
              updatedShape.points = snapSquare(adjustedPoints);
            } else {
              updatedShape.points = adjustedPoints;
            }
          } else {
            updatedShape.points[pointIndex] = { x, y };
          }
          setShapes(updateAtId(shapes, updatedShape));
        } else if (draggingShape) {
          // Update point in an existing shape
          let updatedShapes = [...shapes];
          const draggingShape = shapes.find((s) => s.id === shapeId);
          if (
            draggingShape?.type === "Tie Down T" ||
            draggingShape?.type === "Double T" ||
            draggingShape?.type === "Circle" ||
            draggingShape?.type === "Helipad" ||
            draggingShape?.type === "Compass Rose"
          ) {
            // Fixed adjustment logic for square
            const center = getCenter(draggingShape.points);
            const adjustedPoints = adjustSquare(
              draggingShape.points,
              pointIndex,
              { x, y },
              center
            );
            // if shift is pressed, snap to 45 degree angles
            if (isShiftPressed) {
              //  TODO: implement here
              draggingShape.points = snapSquare(adjustedPoints);
            } else {
              draggingShape.points = adjustedPoints;
            }
          } else {
            draggingShape.points[pointIndex] = { x, y };
          }
          setShapes(updateAtId(updatedShapes, draggingShape));
        }
      } else if (draggingShape) {
        const { shapeId, offset } = draggingShape;
        const rawDx = x - offset.x;
        const rawDy = y - offset.y;

        let updatedShapes = [...shapes];
        const activeShapesList = getActiveShapes();

        const dx = rawDx;
        const dy = rawDy;

        // Check if we should apply snapping (Shift + dragging a single Spot)
        const primaryShape = activeShapesList[0];
        const shouldSnap = isShiftPressed && activeShapesList.length === 1 && primaryShape?.type === "Spot";

        if (shouldSnap && primaryShape) {
          const snapResult = calculateSnapPositions(primaryShape, shapes, dx, dy);

          if (snapResult.didSnap) {
            let newPoints = primaryShape.points.map((point) => ({
              x: point.x + snapResult.snappedX,
              y: point.y + snapResult.snappedY,
            }));

            // Apply rotation if snapped to another spot's angle
            if (snapResult.snappedAngle !== null) {
              const rotatedShape = { ...primaryShape, points: newPoints };
              newPoints = rotateSpotToAngle(rotatedShape, snapResult.snappedAngle);
            }

            updatedShapes = updateAtId(updatedShapes, {
              ...primaryShape,
              points: newPoints,
            });

            // Update guide lines
            setSnapGuideLines(snapResult.guideLines);
          } else {
            // No snap - use constrained delta
            updatedShapes = updateAtId(updatedShapes, {
              ...primaryShape,
              points: primaryShape.points.map((point) => ({
                x: point.x + dx,
                y: point.y + dy,
              })),
            });
            setSnapGuideLines([]);
          }
        } else {
          // Normal drag without snapping
          activeShapesList.forEach((shape) => {
            updatedShapes = updateAtId(updatedShapes, {
              ...shape,
              points: shape.points.map((point) => ({
                x: point.x + dx,
                y: point.y + dy,
              })),
            });
          });

          // Clear guide lines when not snapping
          setSnapGuideLines([]);
        }

        setShapes(updatedShapes);
        setDraggingShape({ shapeId, offset: { x, y } });
      }
    },
    [props]
  );
};
